import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { authApi, initCsrfToken } from '../services/api';
import { hasRole } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';
import LoginHeader from './shared/LoginHeader';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [csrfReady, setCsrfReady] = useState(false);
  const navigate = useNavigate();

  // Initialize CSRF token on component mount
  useEffect(() => {
    const setupCsrf = async () => {
      await initCsrfToken();
      setCsrfReady(true);
    };
    setupCsrf();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authApi.login(email, password);

      if (!hasRole('Admin') && !hasRole('Fair Manager')) {
        setError('Access denied. Admin or Fair Manager privileges required.');
        authApi.logout();
        return;
      }

      // Redirect based on role
      if (hasRole('Fair Manager') && !hasRole('Admin')) {
        navigate('/fairmanager/dashboard');
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Login error:', apiError);
      setError(apiError.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-teal-50">
      {/* Header with site branding - Issue #1440 (Issue #3): Extracted to shared component */}
      <LoginHeader />

      {/* Main content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-moonbone rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-dusk-rose" />
            </div>
            <h2 className="text-3xl font-display font-semibold text-ink">
              🎨 Admin Login
            </h2>
            <p className="mt-2 text-sm font-body text-mid">
              Sign in to manage artist fairs for Local Artist Fairs
            </p>
          </div>
          <form className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-md border border-moonbone" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium font-body text-ink mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  data-testid="admin-login-email-input"
                  className="w-full px-3 py-2 border border-moonbone rounded-md shadow-sm placeholder-mid focus:outline-none focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
                  placeholder="admin@localartistfairs.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium font-body text-ink mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    data-testid="admin-login-password-input"
                    className="w-full px-3 py-2 pr-10 border border-moonbone rounded-md shadow-sm placeholder-mid focus:outline-none focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility-button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-mid hover:text-ink transition-colors" />
                    ) : (
                      <Eye className="h-4 w-4 text-mid hover:text-ink transition-colors" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="text-red-800 text-sm text-center" data-testid="admin-login-error">
                  {error}
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || !csrfReady}
                data-testid="submit-admin-login-button"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium font-body text-white bg-dusk-rose hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dusty-teal disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {!csrfReady ? (
                  <div className="flex items-center">
                    <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Initializing...
                  </div>
                ) : loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in to Admin Panel'
                )}
              </button>
            </div>

            <div className="text-center space-y-2">
              <Link
                to="/admin/reset-password"
                className="text-sm text-dusty-teal hover:text-heather font-medium font-body transition-colors"
              >
                Forgot your password?
              </Link>
              <p className="text-sm font-body text-mid">
                Need help? Contact your system administrator
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}